import { File, EllipsisVertical, TrendingUp, TrendingDown, Loader2, AlertCircle, Plus, X, CheckCircle, CheckCircle2, Table2 } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type DragEvent } from 'react';
import { createPortal } from 'react-dom';
import type { DocInvoice, GlobalFile, PriceIncreaseNotificationItem } from '../../../api/types';
import { formatUploadDate } from '../../../utils/formatDate';
import documentsApi from '../../../api/documentsApi';
import { useIsMobile } from '../../../utils/hooks';
import RelatedTransactionsModal from './RelatedTransactionsModal'
import { formatDocumentTypeLabel, formatFileSize, getTagPillClasses, getTypePillClasses, isSameTagLabel, normalizeTagLabel } from '../utils/tagStyles';
import { formatRelativeTimeAgo } from '../../../utils/formatDate';
import googleSheetsIntegrationApi from '../../../api/googleSheetsIntegrationApi';
import { setWorkspaceDragData } from '../utils/workspaceDrag';
import { computeFixedDropdownNearAnchor, scrollableAncestors } from '../utils/dropdownAnchor';

export interface DocCardFile extends GlobalFile {
  company?: string | null;
  icon?: string | null;
  details?: string | null;
}

interface DocCardProps {
  file: DocCardFile;
  handleShowEdit: (show: boolean) => void;
  onRequestDelete: () => void;
  onRequestMove?: (file: DocCardFile) => void;
  onFileUpdated?: () => void;
  /** When set (for invoice/bill files), opens the line-item confirmation modal. Called with file. */
  onReviewInvoice?: (file: DocCardFile) => void;
  onRequestQboSync?: (file: DocCardFile) => void;
  /** For admin: business ID when viewing another business's documents (used for S3 download). */
  businessId?: string;
  /** Multi-select: whether this file is selected. */
  selected?: boolean;
  /** Multi-select: callback when checkbox toggled. */
  onSelectChange?: (selected: boolean) => void;
  /** Whether to show the selection checkbox. */
  showCheckbox?: boolean;
  /** Available saved labels for this business. */
  availableTagLabels?: string[];
  /** Callback after label catalog changes. */
  onLabelsChanged?: () => void;
  /** Open workspace / file metadata drawer */
  onShowMetadata?: (file: DocCardFile) => void;
  /** Full main-area Google Sheets editor (Finz + AI CFO layout). */
  onGoogleSheetWorkspace?: (file: DocCardFile) => void;
  /** When set, row can be dragged into workspace folders to move files (IDs to include in one drag). */
  workspaceDragFileIds?: string[];
  workspaceDragCategoryKey?: string;
}

const DocCard: React.FC<DocCardProps> = ({
  file,
  handleShowEdit,
  onRequestDelete,
  onRequestMove,
  onFileUpdated,
  onReviewInvoice,
  onRequestQboSync,
  businessId,
  selected,
  onSelectChange,
  showCheckbox,
  availableTagLabels = [],
  onLabelsChanged,
  onShowMetadata,
  onGoogleSheetWorkspace,
  workspaceDragFileIds,
  workspaceDragCategoryKey,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [showRelatedModal, setShowRelatedModal] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [deletingLabel, setDeletingLabel] = useState<string | null>(null);
  const [showPriceIncreaseModal, setShowPriceIncreaseModal] = useState(false);
  const [priceIncreaseItems, setPriceIncreaseItems] = useState<PriceIncreaseNotificationItem[]>([]);
  const [priceIncreaseLoading, setPriceIncreaseLoading] = useState(false);
  const [showPriceDecreaseModal, setShowPriceDecreaseModal] = useState(false);
  const [priceDecreaseItems, setPriceDecreaseItems] = useState<PriceIncreaseNotificationItem[]>([]);
  const [priceDecreaseLoading, setPriceDecreaseLoading] = useState(false);
  const [gsResyncing, setGsResyncing] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const optionsButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const isGoogleSheet = file.source_channel === 'google_sheets';
  const sheetUrl = file.external_document?.spreadsheet_url?.trim();

  const [retryProcessingLoading, setRetryProcessingLoading] = useState(false);
  const isInvoiceLike = file.type === 'invoice' || file.type === 'expenses' || file.type === 'bills';
  const hasLinkedProcessedDoc = Boolean(file.linked_document_id);
  const processingStatus = isInvoiceLike ? (file.processing_status ?? null) : null;
  const hasActiveProcessingStatus =
    processingStatus === 'uploaded' ||
    processingStatus === 'parsing' ||
    processingStatus === 'extracting';
  const isActiveProcessing = Boolean(
    isInvoiceLike &&
    file.processed === false &&
    hasActiveProcessingStatus
  );
  const showProcessingFailed = Boolean(
    isInvoiceLike &&
    file.processed === false &&
    processingStatus === 'failed'
  );
  const processingBadgeLabel =
    processingStatus === 'uploaded'
      ? 'Queued...'
      : processingStatus === 'parsing'
        ? 'Parsing...'
        : processingStatus === 'extracting'
          ? 'Extracting...'
          : 'Processing...';
  const processingTitle =
    processingStatus === 'uploaded'
      ? 'Upload finished. AI processing will start momentarily.'
      : processingStatus === 'parsing'
        ? 'Document is being parsed'
        : processingStatus === 'extracting'
          ? 'Document data is being extracted'
          : 'Document is being processed (OCR and AI extraction)';
  const processingStatusDetail =
    processingStatus === 'uploaded'
      ? 'Uploaded and queued for AI processing'
      : processingStatus === 'parsing'
        ? 'Parsing document contents'
        : processingStatus === 'extracting'
          ? 'Extracting fields and line items'
          : 'Processing document';
  const showPriceFlag = Boolean(file.has_price_increase && !file.price_increase_dismissed_at);
  const showPriceDecreaseFlag = Boolean(file.has_price_decrease && !file.price_decrease_dismissed_at);
  const showWaitingForConfirmation = Boolean(
    isInvoiceLike && file.waiting_for_confirmation
  );
  const retryProcessingDisabled = retryProcessingLoading || isActiveProcessing;
  const showRetryProcessingAction = Boolean(
    isInvoiceLike && (showProcessingFailed || showWaitingForConfirmation || isActiveProcessing)
  );
  const showProcessedIndicator = Boolean(
    isInvoiceLike &&
    hasLinkedProcessedDoc &&
    !isActiveProcessing &&
    !showProcessingFailed &&
    !showWaitingForConfirmation &&
    file.processed !== false
  );
  const showProcessCurrentTypeAction = Boolean(
    isInvoiceLike &&
    !hasLinkedProcessedDoc &&
    !showRetryProcessingAction &&
    !isActiveProcessing
  );
  const processCurrentTypeLabel =
    file.type === 'bills'
      ? 'Process bill'
      : file.type === 'expenses'
        ? 'Process expense'
        : 'Process invoice';
  const isQboSyncEligibleType = file.type === 'invoice' || file.type === 'bills';
  const isQboPendingReview = Boolean(isQboSyncEligibleType && file.waiting_for_confirmation);
  const isQboSyncInProgress = file.qbo_sync_status === 'queued' || file.qbo_sync_status === 'syncing';
  const isQboReadyToSync = Boolean(
    isQboSyncEligibleType &&
    !isQboPendingReview &&
    !isQboSyncInProgress &&
    !isActiveProcessing &&
    !showProcessingFailed &&
    file.processed !== false
  );
  const showQboSyncButton = Boolean(isQboReadyToSync && !file.qbo_synced && onRequestQboSync);
  const invoiceEditLabel = showWaitingForConfirmation
    ? 'Review & confirm'
    : file.type === 'bills'
      ? 'Edit bill'
      : file.type === 'expenses'
        ? 'Edit expense'
        : 'Edit invoice';

  const mapPriceChangeToNotificationItem = (
    item: { description: string; unit_price: number; currency: string; vendor_name: string; previous_most_recent_unit_price: number },
    index: number,
    docId: string | undefined,
    direction: 'increase' | 'decrease'
  ): PriceIncreaseNotificationItem => {
    const prev = item.previous_most_recent_unit_price;
    const next = item.unit_price;
    const verb = direction === 'increase' ? 'increased' : 'decreased';
    const msg = `Unit price ${verb} from ${prev} to ${next} ${item.currency} for ${item.description.slice(0, 50)}${item.description.length > 50 ? "…" : ""} at ${item.vendor_name}.`;
    return {
      id: `fallback-${index}`,
      vendor_name: item.vendor_name,
      invoice_id: docId || "",
      previous_unit_price: prev,
      new_unit_price: next,
      currency: item.currency,
      message: msg,
      created_at: "",
      description: item.description,
    };
  };

  const getFallbackPriceChangeItems = async (
    fid: string,
    direction: 'increase' | 'decrease'
  ): Promise<PriceIncreaseNotificationItem[]> => {
    try {
      let byFile: DocInvoice | undefined;
      try {
        byFile = await documentsApi.getInvoiceByFileId(fid, businessId);
      } catch {
        byFile = await documentsApi.getBillByFileId(fid, businessId);
      }
      const raw = direction === 'increase' ? byFile?.price_increases : byFile?.price_decreases;
      const docId = byFile?._id ?? byFile?.id;
      if (!raw?.length || !docId) return [];
      return raw.map((p, i) => mapPriceChangeToNotificationItem(p, i, docId, direction));
    } catch {
      return [];
    }
  };

  const handleOpenPriceIncreaseModal = async () => {
    const fid = file.file_id ?? (file as GlobalFile & { _id?: string })._id;
    setShowPriceIncreaseModal(true);
    setPriceIncreaseLoading(true);
    if (!fid) {
      setPriceIncreaseItems([]);
      setPriceIncreaseLoading(false);
      return;
    }
    try {
      const fileId = String(fid);
      const res = await documentsApi.getPriceIncreases(fileId, 50, businessId);
      let items = res.price_increases || [];
      if (items.length === 0) {
        items = await getFallbackPriceChangeItems(fileId, 'increase');
      }
      if (items.length === 0 && showPriceFlag) {
        try {
          const repair = await documentsApi.repairPriceFlags({
            fileId,
            businessId,
            limit: 1,
            dryRun: false,
          });
          const repairedRes = await documentsApi.getPriceIncreases(fileId, 50, businessId);
          items = repairedRes.price_increases || [];
          if (items.length === 0) {
            items = await getFallbackPriceChangeItems(fileId, 'increase');
          }
          if ((repair.updated_files || 0) > 0) {
            onFileUpdated?.();
          }
        } catch {
          /* keep empty state if repair fails */
        }
      }
      setPriceIncreaseItems(items);
    } catch (err) {
      console.error(err);
      setPriceIncreaseItems([]);
    } finally {
      setPriceIncreaseLoading(false);
    }
  };

  const handleOpenPriceDecreaseModal = async () => {
    const fid = file.file_id ?? (file as GlobalFile & { _id?: string })._id;
    setShowPriceDecreaseModal(true);
    setPriceDecreaseLoading(true);
    if (!fid) {
      setPriceDecreaseItems([]);
      setPriceDecreaseLoading(false);
      return;
    }
    try {
      const fileId = String(fid);
      const res = await documentsApi.getPriceDecreases(fileId, 50, businessId);
      let items = res.price_decreases || [];
      if (items.length === 0) {
        items = await getFallbackPriceChangeItems(fileId, 'decrease');
      }
      if (items.length === 0 && showPriceDecreaseFlag) {
        try {
          const repair = await documentsApi.repairPriceFlags({
            fileId,
            businessId,
            limit: 1,
            dryRun: false,
          });
          const repairedRes = await documentsApi.getPriceDecreases(fileId, 50, businessId);
          items = repairedRes.price_decreases || [];
          if (items.length === 0) {
            items = await getFallbackPriceChangeItems(fileId, 'decrease');
          }
          if ((repair.updated_files || 0) > 0) {
            onFileUpdated?.();
          }
        } catch {
          /* keep empty state if repair fails */
        }
      }
      setPriceDecreaseItems(items);
    } catch (err) {
      console.error(err);
      setPriceDecreaseItems([]);
    } finally {
      setPriceDecreaseLoading(false);
    }
  };

  const handleRemovePriceFlag = async () => {
    try {
      await documentsApi.updateFile(file.file_id, {
        price_increase_dismissed_at: new Date().toISOString(),
      });
      onFileUpdated?.();
      setShowPriceIncreaseModal(false);
    } catch (err) {
      console.error('Failed to remove price increase flag:', err);
      alert('Could not remove flag. Please try again.');
    }
  };

  const handleRemovePriceDecreaseFlag = async () => {
    try {
      await documentsApi.updateFile(file.file_id, {
        price_decrease_dismissed_at: new Date().toISOString(),
      });
      onFileUpdated?.();
      setShowPriceDecreaseModal(false);
    } catch (err) {
      console.error('Failed to remove price decrease flag:', err);
      alert('Could not remove flag. Please try again.');
    }
  };

  const computeMenuPosition = useCallback((): { top: number; left: number } | null => {
    const el = optionsButtonRef.current;
    if (!el) return null;
    return computeFixedDropdownNearAnchor(el, {
      isMobile,
      dropdownWidth: 176,
      dropdownHeight: isMobile ? 360 : 320,
    });
  }, [isMobile]);

  useLayoutEffect(() => {
    if (!showOptions) {
      setDropdownPosition(null);
      return;
    }
    const measure = () => {
      const pos = computeMenuPosition();
      if (pos) setDropdownPosition(pos);
    };
    measure();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(measure);
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [showOptions, computeMenuPosition]);

  useEffect(() => {
    if (!showOptions) return;
    const reposition = () => {
      const pos = computeMenuPosition();
      if (pos) setDropdownPosition(pos);
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    const el = optionsButtonRef.current;
    const ancestors = scrollableAncestors(el);
    for (const node of ancestors) {
      node.addEventListener('scroll', reposition, true);
    }
    reposition();
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      for (const node of ancestors) {
        node.removeEventListener('scroll', reposition, true);
      }
    };
  }, [showOptions, computeMenuPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (optionsRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setShowOptions(false);
    };
    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOptions]);

  const handleDownload = async () => {
    const openDownload = async (s3Key: string) => {
      const res = await documentsApi.getDownloadUrl(s3Key, true, businessId);
      if (!res?.url) {
        throw new Error('Failed to fetch download link.');
      }
      window.open(res.url, '_blank');
    };

    try {
      await openDownload(file.filename);
    } catch (err) {
      try {
        if (!file.file_id) {
          throw err;
        }
        await Promise.resolve(onFileUpdated?.());
        const latestFile = await documentsApi.getUserGlobalFile(file.file_id, businessId);
        const latestKey = (latestFile?.filename || '').trim();
        if (!latestKey) {
          throw err;
        }
        await openDownload(latestKey);
      } catch (retryErr) {
        console.error('Download failed:', retryErr);
        alert('Could not open this file right now. Please try again.');
      }
    }
  };

  const handlePrimaryOpen = () => {
    if (isInvoiceLike && onReviewInvoice) {
      onReviewInvoice(file);
      return;
    }
    if (isGoogleSheet && sheetUrl) {
      if (onGoogleSheetWorkspace) {
        onGoogleSheetWorkspace(file);
        return;
      }
      if (onShowMetadata) {
        onShowMetadata(file);
        return;
      }
      window.open(sheetUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    void handleDownload();
  };

  const handleGoogleSheetResync = async () => {
    const sid = file.google_sheet_source_document_id;
    if (!sid || gsResyncing) return;
    setGsResyncing(true);
    setShowOptions(false);
    try {
      await googleSheetsIntegrationApi.resync(sid);
      onFileUpdated?.();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || '')
          : e instanceof Error
            ? e.message
            : 'Re-sync failed';
      window.alert(msg);
    } finally {
      setGsResyncing(false);
    }
  };

  const handleRetryProcessing = async () => {
    const fid = file.file_id ?? (file as GlobalFile & { _id?: string })._id;
    if (!fid || retryProcessingLoading) return;
    setRetryProcessingLoading(true);
    setShowOptions(false);
    try {
      await documentsApi.retryFileProcessing(String(fid), businessId);
      onFileUpdated?.();
    } catch (err: any) {
      alert(err?.message || 'Failed to retry processing.');
    } finally {
      setRetryProcessingLoading(false);
    }
  };

  const currentTags = (Array.isArray(file.tag) ? file.tag : []).filter((tag): tag is string => typeof tag === 'string' && !!tag.trim());

  const persistTags = async (nextTags: string[]) => {
    try {
      await documentsApi.updateFile(file.file_id, { tag: nextTags }, businessId);
      onFileUpdated?.();
    } catch (err) {
      console.error('Failed to update tags:', err);
      alert('Could not update tags. Try again.');
    }
  };

  const handleAddTag = async (rawTag: string) => {
    const nextTag = normalizeTagLabel(rawTag);
    if (!nextTag) return;
    if (currentTags.some((tag) => isSameTagLabel(tag, nextTag))) {
      setTagInput('');
      setShowTagInput(false);
      return;
    }
    await persistTags([...currentTags, nextTag]);
    setTagInput('');
    setShowTagInput(false);
    try {
      await documentsApi.addTagLabel(nextTag, businessId);
      onLabelsChanged?.();
    } catch (err) {
      console.error('Failed to upsert tag label:', err);
    }
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    const nextTags = currentTags.filter((tag) => !isSameTagLabel(tag, tagToRemove));
    await persistTags(nextTags);
  };

  const handleDeleteLabel = async (label: string) => {
    if (deletingLabel) return;
    setDeletingLabel(label);
    try {
      await documentsApi.deleteTagLabel(label, businessId);
      onLabelsChanged?.();
      onFileUpdated?.();
    } catch (err: any) {
      alert(err?.message || 'Could not delete label.');
    } finally {
      setDeletingLabel(null);
    }
  };

  const suggestedLabels = availableTagLabels.filter(
    (label) =>
      !currentTags.some((tag) => isSameTagLabel(tag, label)) &&
      label.toLowerCase().includes(tagInput.toLowerCase()),
  );
  const noteLabel = normalizeTagLabel(file.note || file.notes || '');

  const renderQboSyncCell = () => {
    if (!isQboSyncEligibleType) {
      return <span className="text-sm text-gray-400">—</span>;
    }
    if (file.qbo_synced) {
      return (
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700">
          <CheckCircle className="w-4 h-4" />
          Synced
        </span>
      );
    }
    if (isQboSyncInProgress) {
      return (
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700">
          <Loader2 className="w-4 h-4 animate-spin" />
          Syncing...
        </span>
      );
    }
    if (isQboPendingReview) {
      return <span className="text-xs text-amber-700">Pending Confirm</span>;
    }
    if (showQboSyncButton) {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRequestQboSync?.(file);
          }}
          className="inline-flex items-center gap-1 px-6 py-1 rounded border border-gray-300 text-xs text-gray-600 hover:border-gray-400 hover:text-gray-800"
        >
          Sync
        </button>
      );
    }
    return <span className="text-sm text-gray-400">—</span>;
  };

  const priceIncreaseModal = showPriceIncreaseModal ? (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => setShowPriceIncreaseModal(false)}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-semibold text-gray-900">Price increase details for this document</h3>
          <button type="button" className="text-gray-500 hover:text-gray-700" onClick={() => setShowPriceIncreaseModal(false)} aria-label="Close">×</button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {priceIncreaseLoading ? (
            <p className="text-gray-500">Loading…</p>
          ) : priceIncreaseItems.length === 0 ? (
            <p className="text-gray-500">No price increase details for this document.</p>
          ) : (
            <ul className="space-y-3">
              {priceIncreaseItems.map((item) => (
                <li key={item.id} className="text-sm border-b border-gray-100 pb-3 last:border-0">
                  <p className="font-medium text-gray-800">{item.description || 'Line item'}</p>
                  <p className="text-gray-600">{item.vendor_name}</p>
                  <p className="text-amber-700 mt-1">
                    {item.currency} {item.previous_unit_price} → {item.new_unit_price}
                  </p>
                  {item.message && <p className="text-gray-500 text-xs mt-1">{item.message}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button type="button" className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded" onClick={() => setShowPriceIncreaseModal(false)}>Close</button>
          <button type="button" className="px-3 py-1.5 bg-amber-100 text-amber-800 hover:bg-amber-200 rounded" onClick={handleRemovePriceFlag}>Remove flag</button>
        </div>
      </div>
    </div>
  ) : null;

  const priceDecreaseModal = showPriceDecreaseModal ? (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => setShowPriceDecreaseModal(false)}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-semibold text-gray-900">Price decrease details for this document</h3>
          <button type="button" className="text-gray-500 hover:text-gray-700" onClick={() => setShowPriceDecreaseModal(false)} aria-label="Close">×</button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {priceDecreaseLoading ? (
            <p className="text-gray-500">Loading…</p>
          ) : priceDecreaseItems.length === 0 ? (
            <p className="text-gray-500">No price decrease details for this document.</p>
          ) : (
            <ul className="space-y-3">
              {priceDecreaseItems.map((item) => (
                <li key={item.id} className="text-sm border-b border-gray-100 pb-3 last:border-0">
                  <p className="font-medium text-gray-800">{item.description || 'Line item'}</p>
                  <p className="text-gray-600">{item.vendor_name}</p>
                  <p className="text-green-700 mt-1">
                    {item.currency} {item.previous_unit_price} → {item.new_unit_price}
                  </p>
                  {item.message && <p className="text-gray-500 text-xs mt-1">{item.message}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button type="button" className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded" onClick={() => setShowPriceDecreaseModal(false)}>Close</button>
          <button type="button" className="px-3 py-1.5 bg-green-100 text-green-800 hover:bg-green-200 rounded" onClick={handleRemovePriceDecreaseFlag}>Remove flag</button>
        </div>
      </div>
    </div>
  ) : null;

  const canWorkspaceDrag = Boolean(workspaceDragFileIds && workspaceDragFileIds.length > 0);

  const handleWorkspaceDragStart = (e: DragEvent) => {
    if (!workspaceDragFileIds?.length) return;
    const previewLabel =
      workspaceDragFileIds.length > 1
        ? `${workspaceDragFileIds.length} files`
        : (file.original_filename || 'Document');
    setWorkspaceDragData(e.dataTransfer, {
      kind: 'files',
      fileIds: workspaceDragFileIds,
      sourceCategoryKey: workspaceDragCategoryKey,
    }, previewLabel);
  };

  // MOBILE UI
  if (isMobile) {
    return (
      <>
        <div
          draggable={canWorkspaceDrag}
          onDragStart={canWorkspaceDrag ? handleWorkspaceDragStart : undefined}
          className={`mb-3 relative grid grid-cols-2 w-full border rounded-lg p-4 font-medium flex flex-col gap-4 ${showCheckbox ? 'pl-10' : ''} ${canWorkspaceDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
        >
          {showCheckbox && (
            <div className="absolute top-4 left-4">
              <input
                type="checkbox"
                checked={selected ?? false}
                onChange={(e) => onSelectChange?.(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {/* Edit Menu */}
          <div className="absolute top-4 right-4" ref={optionsRef}>
            {showOptions && dropdownPosition && createPortal(
              <div
                ref={dropdownRef}
                className="fixed z-[9999] p-3 rounded gap-2 bg-white flex flex-col font-normal text-sm border shadow-lg min-w-[160px] max-h-[min(320px,70vh)] overflow-y-auto"
                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
              >
                {isGoogleSheet && sheetUrl && onGoogleSheetWorkspace && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOptions(false);
                      onGoogleSheetWorkspace(file);
                    }}
                  >
                    Open in Finz
                  </button>
                )}
                {isGoogleSheet && sheetUrl && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOptions(false);
                      window.open(sheetUrl, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    Open in Google Sheets (new tab)
                  </button>
                )}
                {isGoogleSheet && file.google_sheet_source_document_id && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-50"
                    disabled={gsResyncing}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      void handleGoogleSheetResync();
                    }}
                  >
                    {gsResyncing ? 'Re-syncing…' : 'Re-sync'}
                  </button>
                )}
                {onRequestMove && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowOptions(false); onRequestMove(file); }}
                  >
                    Manage
                  </button>
                )}
                {onReviewInvoice && isInvoiceLike && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowOptions(false); onReviewInvoice(file); }}
                  >
                    {invoiceEditLabel}
                  </button>
                )}
                {showRetryProcessingAction && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleRetryProcessing(); }}
                    disabled={retryProcessingDisabled}
                    title={
                      showWaitingForConfirmation
                        ? 'Run AI extraction again for this document'
                        : showProcessingFailed
                          ? (file.processing_error || 'Processing failed. Retry to try again.')
                          : 'Processing is still in progress'
                    }
                  >
                    {retryProcessingLoading ? 'Retrying…' : isActiveProcessing ? 'Processing...' : 'Retry AI processing'}
                  </button>
                )}
                {showProcessCurrentTypeAction && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleRetryProcessing(); }}
                    disabled={retryProcessingLoading}
                    title={`Run ${file.type === 'bills' ? 'bill' : file.type === 'expenses' ? 'expense' : 'invoice'} processing for this document`}
                  >
                    {retryProcessingLoading ? 'Processing…' : processCurrentTypeLabel}
                  </button>
                )}
                {isInvoiceLike && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowOptions(false); setShowRelatedModal(true); }}
                  >
                    Related document
                  </button>
                )}
                {/* <button type="button" className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowOptions(false); handleShowEdit(true); }}>Edit</button> */}
                {!isGoogleSheet && (
                  <button type="button" className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowOptions(false); handleDownload(); }}>Open file</button>
                )}
                {onShowMetadata && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowOptions(false); onShowMetadata(file); }}
                  >
                    Details
                  </button>
                )}
                <button type="button" className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded text-red-600 cursor-pointer" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowOptions(false); onRequestDelete(); }}>Delete</button>
              </div>,
              document.body
            )}
            <button
              ref={optionsButtonRef}
              type="button"
              aria-label={`Open actions for ${file.original_filename}`}
              aria-expanded={showOptions}
              aria-haspopup="menu"
              onClick={() => setShowOptions(!showOptions)}
              className="shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <EllipsisVertical className="h-4 w-4" />
            </button>
          </div>

          {/* Document */}
          <div className="">
            <div className="text-gray-500 mb-1">Document</div>
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <File className="shrink-0 w-5 h-5" />
              <button
                type="button"
                onClick={handlePrimaryOpen}
                className="underline cursor-pointer hover:text-blue-600 truncate text-left font-medium max-w-full"
              >
                {file.original_filename}
              </button>
              {isActiveProcessing && (
                <span
                  className="shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium"
                  title={processingTitle}
                >
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {processingBadgeLabel}
                </span>
              )}
              {showProcessingFailed && (
                <span
                  className="shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-red-50 text-red-700 text-xs font-medium"
                  title={file.processing_error || 'Document processing failed. You can retry from the actions menu.'}
                >
                  <AlertCircle className="w-3 h-3" />
                  Failed
                </span>
              )}
              {showWaitingForConfirmation && onReviewInvoice && (
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onReviewInvoice(file); }}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReviewInvoice(file); }}
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-red-700 hover:bg-red-200 text-xs font-medium cursor-pointer relative z-10"
                  title="Waiting for confirmation — click to review & confirm"
                >
                  <AlertCircle className="w-3.5 h-3.5" />
                  Review
                </button>
              )}
              {!showWaitingForConfirmation && onReviewInvoice && isInvoiceLike && (
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onReviewInvoice(file); }}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReviewInvoice(file); }}
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs font-medium cursor-pointer relative z-10"
                  title={file.type === 'expenses' ? 'Edit expense details' : (file.type === 'bills' ? 'Edit bill details' : 'Edit invoice details')}
                >
                  Edit
                </button>
              )}
              {showPriceFlag && (
                <button
                  type="button"
                  onClick={handleOpenPriceIncreaseModal}
                  className="shrink-0 p-0.5 rounded text-red-600 hover:bg-red-50"
                  title="Price increase detected – view details"
                >
                  <TrendingUp className="w-4 h-4" />
                </button>
              )}
              {showPriceDecreaseFlag && (
                <button
                  type="button"
                  onClick={handleOpenPriceDecreaseModal}
                  className="shrink-0 p-0.5 rounded text-green-600 hover:bg-green-50"
                  title="Price decrease detected – view details"
                >
                  <TrendingDown className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Upload Date + Options */}
          <div className="">
            <div className="text-gray-500 mb-1">Upload Date</div>
            <div>{formatUploadDate(file.upload_time)}</div>
          </div>

          {/* Type */}
          <div>
            <div className="text-gray-500 mb-1">Type</div>
            <div className="capitalize">{file.type || '–'}</div>
          </div>

          <div>
            <div className="text-gray-500 mb-1">QBO Sync</div>
            <div>{renderQboSyncCell()}</div>
          </div>

          {/* Tags */}
          <div>
            <div className="text-gray-500 mb-1">Tags or Remarks</div>
            <div className="flex flex-wrap gap-2 items-center">
              {currentTags.length > 0 && !showTagInput && currentTags.map((item) => (
                <span key={item} className={`inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full border ${getTagPillClasses(item)}`}>
                  {item}
                  <button type="button" onClick={() => void handleRemoveTag(item)}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {showTagInput && (
                <input
                  type="text"
                  className="w-full border px-2 py-1 text-sm rounded"
                  placeholder="Enter tag..."
                  value={tagInput}
                  autoFocus
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleAddTag(tagInput);
                    if (e.key === 'Escape') setShowTagInput(false);
                  }}
                />
              )}
              {(
                <button
                  className="font-normal underline text-sm text-blue-500"
                  onClick={() => setShowTagInput(!showTagInput)}
                >
                  add tag
                </button>
              )}
            </div>
            {showTagInput && suggestedLabels.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {suggestedLabels.slice(0, 8).map((label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => void handleAddTag(label)}
                    className={`px-2 py-0.5 rounded text-xs border ${getTagPillClasses(label)}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {priceIncreaseModal}
        {priceDecreaseModal}
        {showRelatedModal && file.file_id && (
          <RelatedTransactionsModal
            fileId={file.file_id}
            onClose={() => setShowRelatedModal(false)}
            businessId={businessId}
          />
        )}
      </>
    );
  }

  return (
    <div
      draggable={canWorkspaceDrag}
      onDragStart={canWorkspaceDrag ? handleWorkspaceDragStart : undefined}
      className={`relative grid min-w-[1200px] items-center border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%] tracking-normal ${showCheckbox
        ? 'grid-cols-[56px_minmax(200px,2fr)_minmax(120px,0.95fr)_minmax(168px,1.15fr)_minmax(92px,0.7fr)_minmax(128px,1fr)_minmax(96px,0.85fr)_48px]'
        : 'grid-cols-[minmax(200px,2fr)_minmax(120px,0.95fr)_minmax(168px,1.15fr)_minmax(92px,0.7fr)_minmax(128px,1fr)_minmax(96px,0.85fr)_48px]'
        } ${canWorkspaceDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      {showCheckbox && (
        <div className="pl-6 pr-2 py-3 flex items-center">
          <input
            type="checkbox"
            draggable={false}
            checked={selected ?? false}
            onChange={(e) => onSelectChange?.(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className="pl-6 pr-5 py-3 min-w-0">
        <div className="flex items-start gap-2 min-w-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 min-w-0 text-gray-900">
              <File className="shrink-0 w-4 h-4" />
              <button
                type="button"
                onClick={handlePrimaryOpen}
                className="underline hover:text-blue-600 truncate whitespace-nowrap max-w-full text-left font-medium cursor-pointer"
                title={
                  isInvoiceLike && onReviewInvoice
                    ? `Open ${file.type === 'bills' ? 'bill' : file.type === 'expenses' ? 'expense' : 'invoice'} editor`
                    : canWorkspaceDrag
                      ? 'Drag filename to move into a folder, or click to open'
                      : `View file - ${file.original_filename}`
                }
              >
                {file.original_filename}
              </button>
              {showProcessedIndicator && (
                <span
                  className="shrink-0 inline-flex items-center text-green-600"
                  title="Document processed"
                  aria-label="Document processed"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                </span>
              )}
              {showWaitingForConfirmation && onReviewInvoice && (
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onReviewInvoice(file); }}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReviewInvoice(file); }}
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-red-700 hover:bg-red-200 text-xs font-medium cursor-pointer"
                  title="Waiting for confirmation - click to review and confirm"
                >
                  <AlertCircle className="w-3.5 h-3.5" />
                  Review
                </button>
              )}
              {isActiveProcessing && (
                <span
                  className="shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium"
                  title={processingTitle}
                >
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {processingBadgeLabel}
                </span>
              )}
              {showProcessingFailed && (
                <span
                  className="shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-red-50 text-red-700 text-xs font-medium"
                  title={file.processing_error || 'Document processing failed. You can retry from the actions menu.'}
                >
                  <AlertCircle className="w-3 h-3" />
                  Failed
                </span>
              )}
            </div>
            {file.workspace_classification_status === 'pending' && (
              <div className="mt-1 text-xs text-amber-600 break-words font-medium">
                Organizing…
              </div>
            )}
            <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 min-w-0">
              <span className="truncate">{formatFileSize(file.file_size)}</span>
              {showProcessingFailed && file.processing_error && (
                <span className="truncate text-red-600" title={file.processing_error}>
                  {file.processing_error}
                </span>
              )}
              {showPriceFlag && (
                <button
                  type="button"
                  onClick={handleOpenPriceIncreaseModal}
                  className="inline-flex items-center rounded text-red-600 hover:text-red-700"
                  title="Price increase detected - view details"
                >
                  <TrendingUp className="w-3.5 h-3.5" />
                </button>
              )}
              {showPriceDecreaseFlag && (
                <button
                  type="button"
                  onClick={handleOpenPriceDecreaseModal}
                  className="inline-flex items-center rounded text-green-600 hover:text-green-700"
                  title="Price decrease detected - view details"
                >
                  <TrendingDown className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="absolute right-3 top-1/2 z-[2] -translate-y-1/2" ref={optionsRef}>
            {showOptions && dropdownPosition && createPortal(
              <div
                ref={dropdownRef}
                className="fixed z-[9999] p-3 rounded gap-2 bg-white flex flex-col font-normal text-sm border shadow-lg min-w-[160px] max-h-[min(320px,70vh)] overflow-y-auto"
                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
              >
                {isGoogleSheet && sheetUrl && onGoogleSheetWorkspace && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOptions(false);
                      onGoogleSheetWorkspace(file);
                    }}
                  >
                    Open in Finz
                  </button>
                )}
                {isGoogleSheet && sheetUrl && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOptions(false);
                      window.open(sheetUrl, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    Open in Google Sheets (new tab)
                  </button>
                )}
                {isGoogleSheet && file.google_sheet_source_document_id && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-50"
                    disabled={gsResyncing}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      void handleGoogleSheetResync();
                    }}
                  >
                    {gsResyncing ? 'Re-syncing…' : 'Re-sync'}
                  </button>
                )}
                {onRequestMove && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowOptions(false); onRequestMove(file); }}
                  >
                    Manage
                  </button>
                )}
                {onReviewInvoice && isInvoiceLike && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowOptions(false); onReviewInvoice(file); }}
                  >
                    {invoiceEditLabel}
                  </button>
                )}
                {onShowMetadata && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOptions(false);
                      onShowMetadata(file);
                    }}
                  >
                    Details
                  </button>
                )}
                {showRetryProcessingAction && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleRetryProcessing(); }}
                    disabled={retryProcessingDisabled}
                    title={
                      showWaitingForConfirmation
                        ? 'Run AI extraction again for this document'
                        : showProcessingFailed
                          ? (file.processing_error || 'Processing failed. Retry to try again.')
                          : 'Processing is still in progress'
                    }
                  >
                    {retryProcessingLoading ? 'Retrying…' : isActiveProcessing ? 'Processing...' : 'Retry AI processing'}
                  </button>
                )}
                {showProcessCurrentTypeAction && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleRetryProcessing(); }}
                    disabled={retryProcessingLoading}
                    title={`Run ${file.type === 'bills' ? 'bill' : file.type === 'expenses' ? 'expense' : 'invoice'} processing for this document`}
                  >
                    {retryProcessingLoading ? 'Processing…' : processCurrentTypeLabel}
                  </button>
                )}
                {isInvoiceLike && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowOptions(false); setShowRelatedModal(true); }}
                  >
                    Related document
                  </button>
                )}
                {!isGoogleSheet && (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowOptions(false); handleDownload(); }}
                  >
                    Open file
                  </button>
                )}
                <button type="button" className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded text-red-600 cursor-pointer" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowOptions(false); onRequestDelete(); }}>Delete</button>
              </div>,
              document.body
            )}
            <button
              ref={optionsButtonRef}
              type="button"
              aria-label={`Open actions for ${file.original_filename}`}
              aria-expanded={showOptions}
              aria-haspopup="menu"
              onClick={(e) => {
                e.stopPropagation();
                setShowOptions(!showOptions);
              }}
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <EllipsisVertical className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="pl-4 pr-6 py-3 text-gray-900 whitespace-nowrap">
        {formatUploadDate(file.upload_time)}
      </div>

      <div className="px-3 py-3 min-w-0">
        {isGoogleSheet ? (
          <div className="flex flex-col gap-0.5">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900">
              <Table2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
              Google Sheets
            </span>
            {(() => {
              // Legacy rows (pre-ingestion-status feature) have no
              // ``ingestion_status``; treat them as ingested so the
              // Documents list doesn't spuriously show a spinner for
              // every old row.
              const rawStatus = file.ingestion_status ?? 'ingested';
              if (rawStatus === 'ingesting') {
                return (
                  <span
                    className="inline-flex items-center gap-1 text-xs text-blue-700"
                    title="Finz is reading this Google Sheet now. It will be ready for chat in a moment."
                  >
                    <Loader2
                      className="h-3.5 w-3.5 shrink-0 animate-spin"
                      aria-hidden
                    />
                    Ingesting…
                  </span>
                );
              }
              if (rawStatus === 'failed') {
                const detail = file.ingestion_error || 'Import failed. Try re-importing.';
                return (
                  <span
                    className="inline-flex items-center gap-1 text-xs text-red-700"
                    title={detail}
                  >
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span className="truncate max-w-[180px]">Ingestion failed</span>
                  </span>
                );
              }
              return (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                  <CheckCircle2
                    className="h-3.5 w-3.5 shrink-0 text-emerald-600"
                    aria-hidden
                  />
                  {formatRelativeTimeAgo(file.rag_indexed_at || file.upload_time)
                    ? `Synced • ${formatRelativeTimeAgo(file.rag_indexed_at || file.upload_time)}`
                    : 'Synced'}
                </span>
              );
            })()}
          </div>
        ) : file.source_channel === 'email' ? (
          <span className="text-sm text-gray-700">Email</span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </div>

      <div className="px-3 py-3 flex justify-center">
        <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium capitalize ${getTypePillClasses(file.type)}`}>
          {formatDocumentTypeLabel(file.type)}
        </span>
      </div>

      <div className="px-3 py-3 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5 min-w-0">
          {currentTags.map((item) => (
            <span key={item} className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded ${getTagPillClasses(item)}`}>
              <span className="truncate max-w-[160px]" title={item}>{item}</span>
              <button
                type="button"
                className="text-current/70 hover:text-current"
                onClick={() => void handleRemoveTag(item)}
                aria-label={`Remove ${item}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {noteLabel && (
            <span className="inline-flex items-center px-2.5 py-1 text-xs rounded border bg-gray-50 text-gray-700 border-gray-200 max-w-[220px] truncate" title={noteLabel}>
              {noteLabel}
            </span>
          )}
          {!showTagInput && (
            <button
              type="button"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-dashed border-gray-300 text-xs text-gray-600 hover:border-gray-400 hover:text-gray-800"
              onClick={() => setShowTagInput(true)}
            >
              <Plus className="w-3 h-3" />
              Add
            </button>
          )}
        </div>
        {showTagInput && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="min-w-0 flex-1 border border-gray-300 px-2 py-1 text-sm rounded"
                placeholder="Add label..."
                value={tagInput}
                autoFocus
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleAddTag(tagInput);
                  if (e.key === 'Escape') {
                    setTagInput('');
                    setShowTagInput(false);
                  }
                }}
              />
              <button
                type="button"
                className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
                onClick={() => {
                  setTagInput('');
                  setShowTagInput(false);
                }}
              >
                Close
              </button>
            </div>
            {suggestedLabels.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {suggestedLabels.slice(0, 8).map((label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => void handleAddTag(label)}
                    className={`px-2 py-0.5 rounded text-xs border ${getTagPillClasses(label)}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-4 py-3 flex justify-center">
        {renderQboSyncCell()}
      </div>

      <div className="px-2 py-3" aria-hidden />

      {priceIncreaseModal}
      {priceDecreaseModal}
      {showRelatedModal && file.file_id && (
        <RelatedTransactionsModal
          fileId={file.file_id}
          onClose={() => setShowRelatedModal(false)}
          businessId={businessId}
        />
      )}
    </div>
  );
};

export default DocCard;
